import { useState, useEffect } from "react";
import type { Activity, ActivityStatus } from "./ActivityManager";
import { downloadActivityMemberRegistrationsExcel } from "../../../api/activityRegistrationExport";
import { getUserFacingMessage } from "../../../api/errors";
import {
  copyActivityCheckinPublicUrl,
  openActivityCheckinScreen,
} from "../../../utils/activityCheckinUrl";

type DetailTab = "overview";

interface Props {
  activity: Activity;
  /** 用于拼接现场签到 H5 的 `tenant` 参数 */
  tenantLegacyId: string;
  onClose: () => void;
  onApprove: (id: string) => void;
  onReject: (id: string, note: string) => void;
}

const statusMeta: Record<ActivityStatus, { label: string; color: string; icon: string }> = {
  draft:   { label: "草稿",   color: "text-gray-500",  icon: "ri-draft-line" },
  pending: { label: "待审核", color: "text-amber-600", icon: "ri-timer-line" },
  ongoing: { label: "进行中", color: "text-green-600", icon: "ri-checkbox-circle-line" },
  full:    { label: "已满",   color: "text-red-600",   icon: "ri-error-warning-line" },
  ended:   { label: "已结束", color: "text-gray-400",  icon: "ri-flag-line" },
};

const badgeConfig: Record<ActivityStatus, string> = {
  draft:   "bg-gray-100 text-gray-500",
  pending: "bg-amber-50 text-amber-600",
  ongoing: "bg-green-50 text-green-600",
  full:    "bg-red-50 text-red-600",
  ended:   "bg-gray-100 text-gray-400",
};

interface TimelineStep {
  label: string;
  sublabel: string;
  icon: string;
  state: "done" | "current" | "pending" | "rejected";
}

function buildTimeline(a: Activity): TimelineStep[] {
  const steps: TimelineStep[] = [];

  // Step 1: Draft created
  steps.push({
    label: "创建草稿",
    sublabel: "活动信息已填写完毕",
    icon: "ri-draft-line",
    state: "done",
  });

  // Step 2: Submitted for review
  if (a.status === "draft") {
    steps.push({ label: "提交审核", sublabel: "等待提交审核", icon: "ri-send-plane-line", state: "pending" });
    steps.push({ label: "审核通过", sublabel: "审核后即可发布", icon: "ri-checkbox-circle-line", state: "pending" });
  } else if (a.status === "pending") {
    steps.push({ label: "提交审核", sublabel: a.submittedAt ? `提交于 ${a.submittedAt}` : "已提交，等待审核", icon: "ri-send-plane-line", state: "done" });
    steps.push({ label: "审核中", sublabel: "请管理员尽快完成审核", icon: "ri-loader-4-line", state: "current" });
  } else if (a.status === "ongoing" || a.status === "full") {
    steps.push({ label: "提交审核", sublabel: a.submittedAt ? `提交于 ${a.submittedAt}` : "已提交审核", icon: "ri-send-plane-line", state: "done" });
    steps.push({ label: "审核通过", sublabel: a.approvedAt ? `通过于 ${a.approvedAt}` : "已审核通过，活动正在进行", icon: "ri-checkbox-circle-line", state: "done" });
    steps.push({ label: "活动进行中", sublabel: `${a.enrolled}/${a.capacity} 人`, icon: "ri-group-line", state: "current" });
  } else if (a.status === "ended") {
    steps.push({ label: "提交审核", sublabel: a.submittedAt ?? "已提交", icon: "ri-send-plane-line", state: "done" });
    steps.push({ label: "审核通过", sublabel: a.approvedAt ?? "已通过", icon: "ri-checkbox-circle-line", state: "done" });
    steps.push({ label: "活动已结束", sublabel: `累计参与 ${a.enrolled} 人`, icon: "ri-flag-line", state: "done" });
  }

  // Rejection note
  if (a.rejectionNote) {
    steps.push({
      label: "曾被退回",
      sublabel: `退回原因：${a.rejectionNote}`,
      icon: "ri-close-circle-line",
      state: "rejected",
    });
  }

  return steps;
}

export default function ActivityDetailModal({
  activity,
  tenantLegacyId,
  onClose,
  onApprove,
  onReject,
}: Props) {
  const [detailTab, setDetailTab] = useState<DetailTab>("overview");
  const [showReject, setShowReject] = useState(false);
  const [rejectNote, setRejectNote] = useState("");
  const [approveConfirm, setApproveConfirm] = useState(false);
  const [checkinCopyTip, setCheckinCopyTip] = useState<string | null>(null);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportTip, setExportTip] = useState<string | null>(null);

  useEffect(() => {
    setDetailTab("overview");
  }, [activity.id]);

  const timeline = buildTimeline(activity);
  const fill = Math.round((activity.enrolled / activity.capacity) * 100);
  const sm = statusMeta[activity.status];
  const onlineFeeFen = Number(activity.onlinePayAmountFen ?? 0) || 0;
  const onlineDepositFen = Number(activity.onlinePayDepositAmountFen ?? 0) || 0;
  const isOnlinePay =
    (activity.expenseType ?? "").trim() === "在线支付" &&
    (onlineFeeFen > 0 || onlineDepositFen > 0);
  const collectedYuan = isOnlinePay ? ((onlineFeeFen * (Number(activity.enrolled) || 0)) / 100).toFixed(2) : "0.00";

  const handleApprove = () => {
    onApprove(activity.id);
    setApproveConfirm(false);
    onClose();
  };

  const handleReject = () => {
    if (!rejectNote.trim()) return;
    onReject(activity.id, rejectNote.trim());
    setShowReject(false);
    setRejectNote("");
    onClose();
  };

  const handleCopyCheckinH5 = async () => {
    const ok = await copyActivityCheckinPublicUrl(activity.id, tenantLegacyId);
    setCheckinCopyTip(ok ? "已复制签到动态码 H5 地址" : "复制失败，请检查浏览器权限或使用「打开签到大屏」后从地址栏复制");
    setTimeout(() => setCheckinCopyTip(null), 2600);
  };

  const handleDownloadMemberExcel = async () => {
    if (exportingExcel) return;
    setExportingExcel(true);
    setExportTip(null);
    try {
      const blob = await downloadActivityMemberRegistrationsExcel(tenantLegacyId, activity.id);
      const safe = String(activity.title || activity.id).slice(0, 40).replace(/[\\/:*?"<>|]/g, "_");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `会员报名表_${safe}_${Date.now()}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      setExportTip("已开始下载会员报名 Excel");
      setTimeout(() => setExportTip(null), 2600);
    } catch (e) {
      setExportTip(getUserFacingMessage(e, "导出失败，请稍后重试"));
      setTimeout(() => setExportTip(null), 3200);
    } finally {
      setExportingExcel(false);
    }
  };

  return (
    <>
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-calendar-event-line text-red-600 text-lg"></i>
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-gray-900 truncate">{activity.title}</h3>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badgeConfig[activity.status]}`}>{sm.label}</span>
                {activity.activityType && <span className="text-xs text-gray-400">{activity.activityType}</span>}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100 flex-shrink-0">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-3 pb-0 border-b border-gray-100 flex-shrink-0 flex gap-1">
          {([
            { id: "overview" as const, label: "活动概况", icon: "ri-layout-grid-line" },
          ] as const).map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setDetailTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded-t-xl border-b-2 -mb-px transition-colors cursor-pointer ${
                detailTab === t.id
                  ? "border-red-600 text-red-600 bg-red-50/50"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <i className={`${t.icon} text-base`}></i>
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto">
          {detailTab === "overview" ? (
          <div className="p-6 grid grid-cols-2 gap-6">
            {/* Left: Info */}
            <div className="space-y-4">
              <div>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">活动信息</h4>
                <div className="space-y-2.5">
                  {[
                    { icon: "ri-calendar-line", label: "活动日期", value: activity.date },
                    { icon: "ri-map-pin-line", label: "活动地点", value: activity.location },
                      { icon: "ri-group-line", label: "容量上限", value: `${activity.capacity} 人` },
                    activity.expenseType
                      ? { icon: "ri-money-cny-circle-line", label: "费用类型", value: activity.expenseType }
                      : null,
                    activity.expenseType === "在线支付" && (activity.onlinePayAmountFen ?? 0) > 0
                      ? {
                          icon: "ri-wechat-pay-line",
                          label: "基础费用",
                          value: `¥${((activity.onlinePayAmountFen ?? 0) / 100).toFixed(2)}`,
                        }
                      : null,
                    activity.expenseType === "在线支付" && (activity.onlinePayDepositAmountFen ?? 0) > 0
                      ? {
                          icon: "ri-refund-2-line",
                          label: "报名押金",
                          value: `¥${((activity.onlinePayDepositAmountFen ?? 0) / 100).toFixed(2)}`,
                        }
                      : null,
                    isOnlinePay
                      ? {
                          icon: "ri-bank-card-line",
                          label: "收款汇总",
                          value: `¥${collectedYuan}（${activity.enrolled} 人）`,
                        }
                      : null,
                    activity.targetAudience?.length
                      ? { icon: "ri-user-follow-line", label: "参与对象", value: activity.targetAudience.join("、") }
                      : null,
                  ].filter(Boolean).map((item) => item && (
                    <div key={item.label} className="flex items-start gap-2.5">
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className={`${item.icon} text-gray-400 text-sm`}></i>
                      </div>
                      <div>
                        <span className="text-xs text-gray-400">{item.label}</span>
                        <p className="text-sm text-gray-700 font-medium">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Enrollment progress */}
              <div className="bg-gray-50 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-500 font-medium">参与进度</span>
                  <span className="text-xs font-bold text-gray-700">{activity.enrolled} / {activity.capacity}</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-1">
                  <div className="h-full bg-gradient-to-r from-red-500 to-red-400 rounded-full transition-all" style={{ width: `${fill}%` }}></div>
                </div>
                <p className="text-xs text-gray-400">{fill}% 参与率</p>
                {activity.status !== "draft" && activity.status !== "pending" ? (
                  <div className="mt-3 pt-3 border-t border-gray-200/80">
                    <button
                      type="button"
                      disabled={exportingExcel}
                      onClick={() => void handleDownloadMemberExcel()}
                      className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-green-200 bg-white px-3 py-2 text-xs font-semibold text-green-700 hover:bg-green-50 cursor-pointer transition-colors disabled:opacity-50 whitespace-nowrap"
                    >
                      <i className="ri-file-excel-2-line"></i>
                      {exportingExcel ? "正在导出…" : "下载会员报名 Excel"}
                    </button>
                    <p className="mt-1.5 text-[11px] text-gray-400 leading-relaxed">
                      若活动关联了协会，将按协会名称分 Sheet 生成名单
                    </p>
                    {exportTip ? <p className="mt-1 text-xs text-gray-600">{exportTip}</p> : null}
                  </div>
                ) : null}
              </div>

              {(activity.status === "ongoing" || activity.status === "full") && (
                <div className="rounded-2xl border border-indigo-100 bg-indigo-50/70 p-4 space-y-3">
                  <div>
                    <h4 className="text-xs font-semibold text-indigo-900 flex items-center gap-1.5">
                      <i className="ri-qr-code-line"></i> 现场签到动态码（H5）
                    </h4>
                    <p className="text-xs text-indigo-800/85 mt-1.5 leading-relaxed">
                      新窗口打开签到大屏供会员扫码；链接含租户与活动 ID，可发给现场工作人员在平板或副屏上打开。
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => openActivityCheckinScreen(activity.id, tenantLegacyId)}
                      className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-700 cursor-pointer transition-colors whitespace-nowrap"
                    >
                      <i className="ri-external-link-line"></i> 打开签到大屏
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleCopyCheckinH5()}
                      className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-indigo-200 bg-white px-4 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-50 cursor-pointer transition-colors whitespace-nowrap"
                    >
                      <i className="ri-file-copy-line"></i> 复制 H5 链接
                    </button>
                  </div>
                  {checkinCopyTip ? (
                    <p className="text-xs text-indigo-800/90">{checkinCopyTip}</p>
                  ) : null}
                </div>
              )}

              {/* Description */}
              {activity.description && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">活动简介</h4>
                  <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 rounded-xl p-3">{activity.description}</p>
                </div>
              )}

              {/* Rejection note */}
              {activity.rejectionNote && (
                <div className="bg-red-50 border border-red-100 rounded-xl p-3">
                  <p className="text-xs font-semibold text-red-600 mb-1 flex items-center gap-1">
                    <i className="ri-close-circle-line"></i> 退回原因
                  </p>
                  <p className="text-xs text-red-600 leading-relaxed">{activity.rejectionNote}</p>
                </div>
              )}

            </div>

            {/* Right: Timeline */}
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-4">审批流程记录</h4>
              <div className="relative">
                {timeline.map((step, idx) => {
                  const isLast = idx === timeline.length - 1;
                  const stateStyles = {
                    done:     { dot: "bg-green-500 border-green-500", icon: "text-white", line: "bg-green-200", text: "text-gray-800" },
                    current:  { dot: "bg-amber-500 border-amber-500 animate-pulse", icon: "text-white", line: "bg-gray-100", text: "text-amber-700" },
                    pending:  { dot: "bg-white border-gray-200", icon: "text-gray-300", line: "bg-gray-100", text: "text-gray-400" },
                    rejected: { dot: "bg-red-500 border-red-500", icon: "text-white", line: "bg-red-100", text: "text-red-700" },
                  }[step.state];

                  return (
                    <div key={idx} className="flex gap-3">
                      <div className="flex flex-col items-center flex-shrink-0">
                        <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${stateStyles.dot}`}>
                          <i className={`${step.icon} text-sm ${stateStyles.icon}`}></i>
                        </div>
                        {!isLast && <div className={`w-0.5 flex-1 min-h-[24px] mt-1 ${stateStyles.line}`}></div>}
                      </div>
                      <div className={`pb-5 flex-1 ${isLast ? "" : ""}`}>
                        <p className={`text-sm font-semibold ${stateStyles.text}`}>{step.label}</p>
                        <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{step.sublabel}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Tags / doc sign */}
              {activity.tags && (
                <div className="mt-4">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">活动标签</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {activity.tags.split(",").map((tag) => (
                      <span key={tag.trim()} className="text-xs px-2.5 py-1 bg-gray-100 text-gray-600 rounded-full">{tag.trim()}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          ) : null}
        </div>

        {/* Footer actions */}
        {activity.status === "pending" && (
          <div className="px-6 py-4 border-t border-gray-100 flex items-center gap-3 flex-shrink-0 bg-amber-50/50">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-amber-700">此活动正在等待审核</p>
              <p className="text-xs text-amber-600 mt-0.5">审核通过后活动将进入「进行中」状态</p>
            </div>
            <button
              onClick={() => setShowReject(true)}
              className="flex items-center gap-1.5 px-4 py-2 bg-white border border-gray-200 text-gray-600 text-sm rounded-xl hover:bg-gray-50 cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className="ri-close-circle-line"></i> 退回修改
            </button>
            <button
              onClick={() => setApproveConfirm(true)}
              className="flex items-center gap-1.5 px-5 py-2 bg-green-600 text-white text-sm rounded-xl hover:bg-green-700 cursor-pointer transition-colors whitespace-nowrap font-medium"
            >
              <i className="ri-checkbox-circle-line"></i> 审核通过
            </button>
          </div>
        )}
      </div>

      {/* Approve Confirm */}
      {approveConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-2xl p-6 w-[380px]">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-checkbox-circle-line text-green-600 text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-800 mb-1">确认审核通过？</h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  通过后活动将自动设为「进行中」状态，会员可查看该活动。
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setApproveConfirm(false)} className="flex-1 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={handleApprove} className="flex-1 py-2.5 text-sm text-white bg-green-600 rounded-xl hover:bg-green-700 cursor-pointer whitespace-nowrap">确认通过</button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showReject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-2xl p-6 w-[400px]">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-close-circle-line text-red-500 text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-800 mb-1">退回修改</h3>
                <p className="text-sm text-gray-500 leading-relaxed">活动将退回草稿状态，请说明需要修改的内容。</p>
              </div>
            </div>
            <div className="mb-5">
              <label className="block text-xs font-semibold text-gray-600 mb-2">退回原因 *</label>
              <textarea
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                placeholder="请说明需要修改的具体内容，如：活动日期冲突、地点未确认等..."
                maxLength={200}
                rows={4}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:border-red-400"
              />
              <p className="text-xs text-gray-400 text-right mt-1">{rejectNote.length}/200</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setShowReject(false); setRejectNote(""); }} className="flex-1 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={handleReject} disabled={!rejectNote.trim()} className="flex-1 py-2.5 text-sm text-white bg-red-600 rounded-xl hover:bg-red-700 cursor-pointer disabled:opacity-50 whitespace-nowrap">确认退回</button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
